import Link from "next/link";

import LemonMark from "@/components/ui/LemonMark";
import { socialLinks } from "@/components/ui/SocialIcons";
import { CtaLink } from "@/components/CtaLink";
import AnonAnalyticsToggle from "@/components/layout/AnonAnalyticsToggle";
import { STUDIO_URL } from "@/lib/urls";

const SIGNUP_URL = `${STUDIO_URL}/signup`;

type FooterLink = { href: string; label: string; cta?: boolean };

const columns: { title: string; links: FooterLink[] }[] = [
  {
    title: "Product",
    links: [
      { href: "/platform", label: "Platform" },
      { href: "/pricing", label: "Pricing" },
      { href: SIGNUP_URL, label: "Get Started", cta: true },
    ],
  },
  {
    title: "Services",
    links: [
      { href: "/consulting", label: "AI Consulting" },
      { href: "/training", label: "AI Training" },
      { href: "/whitelabel", label: "White Label" },
    ],
  },
  {
    title: "Company",
    links: [
      { href: "/about", label: "About" },
      { href: "/blog", label: "Blog" },
      { href: "/book", label: "Contact" },
    ],
  },
  {
    title: "Community",
    links: [
      { href: "/learn", label: "Learn" },
      { href: "/community", label: "Community" },
      { href: "/forum", label: "Forum" },
      { href: "/faqs", label: "FAQs" },
    ],
  },
];

const legalLinks = [
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/terms", label: "Terms of Use" },
  { href: "/cookies", label: "Cookie Policy" },
  { href: "/acceptable-use", label: "Acceptable Use" },
  { href: "/disclaimer", label: "Disclaimer" },
];

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-section" role="contentinfo">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 pt-16 pb-4">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-6">
          <div className="lg:col-span-5 lg:pr-10">
            <Link
              href="/"
              className="inline-flex items-center gap-3"
              aria-label="LaunchLemonade home"
            >
              <LemonMark size={34} />
              <span className="font-heading text-[22px] font-semibold tracking-tight text-on-dark">
                LaunchLemonade
              </span>
            </Link>
            <p className="mt-3 max-w-[34ch] text-[15px] leading-relaxed text-on-dark/60">
              LaunchLemonade handles email, research, admin and reporting with
              permissions, audit trails and approval workflows built in.
            </p>

            <ul className="mt-6 flex flex-wrap items-center gap-3">
              {socialLinks.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-hairline text-on-dark/60 transition-colors hover:border-on-dark/30 hover:text-on-dark"
                  >
                    <s.Icon size={16} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-[30%_1fr] gap-x-3 gap-y-10 sm:grid-cols-4 sm:gap-10 lg:col-span-7">
            {columns.map((col) => (
              <nav key={col.title} aria-label={`${col.title} navigation`}>
                <h3 className="font-heading text-[16px] font-semibold text-on-dark">
                  {col.title}
                </h3>
                <ul className="mt-5 space-y-4">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.cta ? (
                        <CtaLink
                          location="footer"
                          label={l.label}
                          href={l.href}
                          trackLead
                          className="text-[15px] text-on-dark/60 transition-colors hover:text-on-dark"
                        >
                          {l.label}
                        </CtaLink>
                      ) : l.href.startsWith("http") ? (
                        <a
                          href={l.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[15px] text-on-dark/60 transition-colors hover:text-on-dark"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          href={l.href}
                          className="text-[15px] text-on-dark/60 transition-colors hover:text-on-dark"
                        >
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-6 border-t border-hairline pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
            <p className="font-heading text-[12px] tracking-tight text-on-dark/50">
              {`© ${new Date().getFullYear()} LaunchLemonade. All rights reserved. London, UK`}
            </p>
          </div>

          <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-x-8">
            {legalLinks.map((l) => (
              <li key={l.href} className="shrink-0">
                <Link
                  href={l.href}
                  className="font-heading text-[12px] tracking-tight text-on-dark/50 transition-colors hover:text-on-dark"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li className="shrink-0">
              <AnonAnalyticsToggle className="font-heading text-[12px] tracking-tight text-on-dark/50 transition-colors hover:text-on-dark" />
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
